import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { applicationSourceLabel, type MetricKey, type Proficiency, type ProficiencyCard } from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { SkeletonLines } from '../../../components/Skeleton';
import { FilteredEmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { EMPTY, formatNumber } from '../../../lib/format';
import { useCanView } from '../../../stores/session';
import { DeepLink } from '../../notifications/components/DeepLink';
import { useProficiency, type AnalyticsUrl } from '../api/analytics-api';
import { AnalyticsFilters } from '../components/AnalyticsFilters';
import { DictionaryLine, DrillCount, DrillLatency, DrillRate, ExportLinks } from '../components/AnalyticsBits';
import { moneyList } from '../report-format';
import styles from '../Reports.module.css';

const REPORT = 'recruiter_proficiency';

/**
 * RPT-03 — recruiter proficiency (master plan S29, §L27 RPT-03a). A profile card per recruiter,
 * deliberately never a ranking: the cards come in name order and nothing on the page sorts them.
 * A recruiter sees only their own card (§L23).
 */
export function ProficiencyPage(): ReactNode {
  const canView = useCanView('reports_own');
  const search: AnalyticsUrl = useSearch({ from: '/app-layout/reports/proficiency' });
  const navigate = useNavigate();
  const report = useProficiency(search, canView);
  const data = report.data;

  if (!canView || report.error?.status === 403) return <ForbiddenState resource="recruiter proficiency" contact="Your admin" />;
  if (report.error && !data) return <ServerErrorState onRetry={() => void report.refetch()} />;

  const setFilters = (next: AnalyticsUrl): void => void navigate({ to: '/reports/proficiency', search: next, replace: true });
  const isOwn = data?.audience === 'recruiter';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{isOwn ? 'My proficiency' : 'Recruiter proficiency'}</h1>
          <p className={styles.lead}>A profile per recruiter, never a ranking. Every figure opens the records behind it.</p>
        </div>
        {data && <ExportLinks report={REPORT} filters={data.filters} />}
      </header>

      {data && <AnalyticsFilters filters={data.filters} audience={data.audience} onChange={setFilters} roleFamilies={data.roleFamilies} />}

      {!data ? (
        <Card>
          <SkeletonLines count={8} />
        </Card>
      ) : (
        <Cards data={data} onClear={() => setFilters({ period: data.filters.period })} />
      )}
    </div>
  );
}

function Cards({ data, onClear }: { readonly data: Proficiency; readonly onClear: () => void }): ReactNode {
  const usable = data.benchmarks.filter((row) => row.isUsable);
  return (
    <>
      <DictionaryLine filters={data.filters} version={data.dictionaryVersion} publishedOn={data.dictionaryPublishedOn} />
      <p className={styles.meta}>
        Listed by name. Time to fill is compared with the organisation’s own median for each role family over the last 12 months
        {usable.length > 0 ? `: ${usable.map((row) => `${row.roleFamily} ${row.medianActiveDays ?? EMPTY} working days`).join(' · ')}` : ' — no family has 3 fills yet, so nothing is indexed'}.
      </p>
      {data.cards.length === 0 ? (
        <FilteredEmptyState onClearFilters={onClear} filterCount={1} />
      ) : (
        <div className={styles.profiles}>
          {data.cards.map((card) => (
            <ProfileCard key={card.userId} card={card} data={data} />
          ))}
        </div>
      )}
    </>
  );
}

function ProfileCard({ card, data }: { readonly card: ProficiencyCard; readonly data: Proficiency }): ReactNode {
  const filters = data.filters;
  const count = (figure: ProficiencyCard['applications']): ReactNode => <DrillCount figure={figure} report={REPORT} filters={filters} />;
  const rate = (figure: ProficiencyCard['joining']): ReactNode => <DrillRate figure={figure} report={REPORT} filters={filters} />;
  const latency = (figure: ProficiencyCard['timeToFill']): ReactNode => <DrillLatency figure={figure} report={REPORT} filters={filters} />;
  return (
    <Card title={card.name} meta={`${formatNumber(card.activeRequisitions.value)} active ${card.activeRequisitions.value === 1 ? 'requisition' : 'requisitions'}`}>
      <dl className={styles.profileFigures}>
        <Figure metric="active_requisitions" label="Active requisitions">{count(card.activeRequisitions)}</Figure>
        <Figure metric="source_mix" label="Applications">{count(card.applications)}</Figure>
        <Figure metric="recruiter_lag" label="Recruiter-controlled lag">{latency(card.recruiterLag)}</Figure>
        <Figure metric="time_to_fill" label="Time to fill">{latency(card.timeToFill)}</Figure>
        <Figure metric="time_to_fill_index" label="Against the role family">
          <span className={styles.figure}>{card.timeToFillIndex === null ? EMPTY : `${card.timeToFillIndex.toFixed(2)}×`}</span>
          <span className={styles.cellMeta}>{card.indexedFills === 0 ? 'No benchmark yet' : `${card.indexedFills} ${card.indexedFills === 1 ? 'fill' : 'fills'}; 1.00 is typical`}</span>
        </Figure>
        <Figure metric="screen_to_interview" label="Screened → interviewed">{rate(card.screenToInterview)}</Figure>
        <Figure metric="interview_to_offer" label="Interviewed → offered">{rate(card.interviewToOffer)}</Figure>
        <Figure metric="offer_acceptance" label="Offer acceptance">{rate(card.offerAcceptance)}</Figure>
        <Figure metric="joining_rate" label="Joining">{rate(card.joining)}</Figure>
        <Figure metric="sla_breaches" label="SLA breaches">{count(card.slaBreaches)}</Figure>
        <Figure metric="overdue_follow_ups" label="Overdue follow-ups">{count(card.overdueFollowUps)}</Figure>
        <Figure metric="process_withdrawals" label="Process and experience withdrawals">{count(card.processWithdrawals)}</Figure>
        <Figure metric="cost_per_hire" label="Cost per hire">
          <span className={styles.figure}>{moneyList(card.costPerHire)}</span>
          <span className={styles.cellMeta}>Spend {moneyList(card.spend)}</span>
        </Figure>
        <Figure metric="pool_sourced" label="Hires from talent pools">{rate(card.poolSourced)}</Figure>
        <Figure metric="screening_speed" label="Screening">
          <span className={styles.figure}>{card.screening.medianSecondsPerCard === null ? EMPTY : `${card.screening.medianSecondsPerCard} s a CV`}</span>
          <span className={styles.cellMeta}>{formatNumber(card.screening.cardsSorted)} sorted · median</span>
        </Figure>
      </dl>
      {card.sourceMix.length > 0 && (
        <div className={styles.mix} aria-label="Source mix">
          {card.sourceMix.map((row) => (
            <Chip key={row.source} tone="neutral">
              {applicationSourceLabel(row.source)} {formatNumber(row.count)}
            </Chip>
          ))}
        </div>
      )}
    </Card>
  );
}

/** One figure, its label linking to its definition (06-qol §12). */
function Figure({ metric, label, children }: { readonly metric: MetricKey; readonly label: string; readonly children: ReactNode }): ReactNode {
  return (
    <div>
      <dt>
        <DeepLink to={`/reports/metrics#${metric}`}>{label}</DeepLink>
      </dt>
      <dd className={styles.rate}>{children}</dd>
    </div>
  );
}
